import { toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import MainLayout from '@/components/layout/MainLayout';
import { OverduePaymentsTable } from '@/components/reports/OverduePaymentsTable';
import { ReportPrintButton } from '@/components/reports/ReportPrintButton';
import { useDialog } from '@/hooks/useDialog';

export default function OverdueOwnersReport() {
  const { showAlert } = useDialog();
  const reportRef = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<'excel' | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/reports/overdue-payments?type=propietario');
        setRows(res.data?.data?.rows || res.data?.rows || []);
      } catch (error) {
        console.error('Error loading report:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exportExcel = async () => {
    setExporting('excel');
    try {
      const res = await api.get('/reports/overdue-payments?export=excel&type=propietario');
      const filePath = res.data?.data?.file || res.data?.file;

      if (filePath) {
        const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
        const fileUrl = `${baseURL}/${filePath}`;

        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          withCredentials: true
        });

        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `alquileres-atrasados-propietarios-${toLocalDateKey(new Date())}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar Excel. Por favor, intente nuevamente.',
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <MainLayout compact>
      <div className="w-full space-y-5">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Link to="/reports" className="hover:text-gray-900">Reportes</Link>
            <span>/</span>
            <span className="text-gray-900">Alquileres Atrasados - Propietarios</span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-3xl">🔴</span>
                Alquileres Atrasados - Propietarios
              </h1>
              <p className="text-gray-600">Pagos a propietarios pendientes y vencidos</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ReportPrintButton title="Alquileres Atrasados - Propietarios" containerRef={reportRef} disabled={loading || exporting !== null || rows.length === 0} />
              <ReportPrintButton
                variant="pdf"
                title="Alquileres Atrasados - Propietarios"
                containerRef={reportRef}
                disabled={loading || exporting !== null || rows.length === 0}
              />
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={exportExcel}
                disabled={exporting !== null}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {exporting === 'excel' ? 'Exportando...' : 'Exportar Excel'}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div ref={reportRef} className="bg-white border rounded-lg p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
                <p className="text-gray-600">Cargando reporte...</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos atrasados</h3>
              <p className="text-gray-600">Todos los pagos a propietarios están al día</p>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{rows.length}</span> registro{rows.length !== 1 ? 's' : ''} encontrado{rows.length !== 1 ? 's' : ''}
                </p>
              </div>
              <OverduePaymentsTable rows={rows} personType="propietario" />
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
